import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Copy, Check, MoreVertical, Phone, Settings } from "lucide-react";
import { toast } from "sonner";
import { VoiceSettingsDialog } from "./VoiceSettingsDialog";

interface PhoneNumber {
  id: string;
  twilio_phone_number: string;
  display_name: string;
  campaign_source?: string;
  forward_to_number: string;
  actual_forward_number?: string;
  county: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  sms_enabled?: boolean;
}

interface VoiceSetting {
  id: string;
  phone_number_id: string;
}

export const PhoneNumbersManager = () => {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNumber, setEditingNumber] = useState<PhoneNumber | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [isVoiceSettingsOpen, setIsVoiceSettingsOpen] = useState(false);
  const [voiceSettingsNumber, setVoiceSettingsNumber] = useState<PhoneNumber | null>(null);

  const [formData, setFormData] = useState({
    twilio_phone_number: "",
    display_name: "",
    campaign_source: "",
    county: "Suffolk",
    is_active: true,
    notes: "",
  });

  useEffect(() => {
    loadPhoneNumbers();
    loadVoiceSettings();
  }, []);

  const loadPhoneNumbers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("phone_numbers")
      .select(`
        *,
        sms_settings!sms_settings_phone_number_id_fkey(sms_enabled),
        voice_settings!voice_settings_phone_number_id_fkey(forward_number, secondary_forward_number)
      `)
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load phone numbers");
      console.error(error);
    } else {
      // Flatten sms_enabled and forward_number from nested relationships
      const formattedData = (data || []).map(number => {
        const smsSettings = (number as any).sms_settings;
        const voiceSettings = (number as any).voice_settings;
        
        return {
          ...number,
          sms_enabled: Array.isArray(smsSettings) 
            ? smsSettings[0]?.sms_enabled 
            : smsSettings?.sms_enabled || false,
          actual_forward_number: Array.isArray(voiceSettings)
            ? voiceSettings[0]?.forward_number
            : voiceSettings?.forward_number || number.forward_to_number
        };
      });
      setPhoneNumbers(formattedData);
    }
    setLoading(false);
  };

  const loadVoiceSettings = async () => {
    const { data } = await supabase
      .from("voice_settings")
      .select("id, phone_number_id")
      .order("created_at");
    
    setVoiceSettings(data || []);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Use a default forward number that will be overridden by voice_settings
    const defaultForwardNumber = formData.county === 'Nassau' ? '+15167269555' : '+16317107600';

    if (editingNumber) {
      const { error } = await supabase
        .from("phone_numbers")
        .update({
          ...formData,
          forward_to_number: defaultForwardNumber
        })
        .eq("id", editingNumber.id);

      if (error) {
        toast.error("Failed to update phone number");
        console.error(error);
      } else {
        toast.success("Phone number updated successfully");
        setIsDialogOpen(false);
        resetForm();
        loadPhoneNumbers();
      }
    } else {
      const { error } = await supabase
        .from("phone_numbers")
        .insert([{
          ...formData,
          forward_to_number: defaultForwardNumber
        }]);

      if (error) {
        toast.error("Failed to add phone number");
        console.error(error);
      } else {
        toast.success("Phone number added successfully. Configure voice settings via the 3-dot menu.");
        setIsDialogOpen(false);
        resetForm();
        loadPhoneNumbers();
      }
    }
  };

  const handleEdit = (number: PhoneNumber) => {
    setEditingNumber(number);
    setFormData({
      twilio_phone_number: number.twilio_phone_number,
      display_name: number.display_name,
      campaign_source: number.campaign_source || "",
      county: number.county,
      is_active: number.is_active,
      notes: number.notes || "",
    });
    setIsDialogOpen(true);
  };

  const handleVoiceSettings = (number: PhoneNumber) => {
    setVoiceSettingsNumber(number);
    setIsVoiceSettingsOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this phone number?")) return;

    const { error } = await supabase
      .from("phone_numbers")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Failed to delete phone number");
      console.error(error);
    } else {
      toast.success("Phone number deleted successfully");
      loadPhoneNumbers();
    }
  };

  const resetForm = () => {
    setFormData({
      twilio_phone_number: "",
      display_name: "",
      campaign_source: "",
      county: "Suffolk",
      is_active: true,
      notes: "",
    });
    setEditingNumber(null);
  };

  const copyWebhookUrl = () => {
    const webhookUrl = "https://ltugnhpocjzhouvvciek.supabase.co/functions/v1/twilio-voice-webhook";
    navigator.clipboard.writeText(webhookUrl);
    setCopiedWebhook(true);
    toast.success("Webhook URL copied to clipboard");
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  const formatPhoneNumber = (phone: string) => {
    return phone.replace(/^\+1(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Phone Numbers</h2>
          <p className="text-muted-foreground">
            Manage your Twilio phone numbers and campaign tracking
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Phone Number
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingNumber ? "Edit Phone Number" : "Add New Phone Number"}
              </DialogTitle>
              <DialogDescription>
                Configure a new Twilio phone number for call tracking and forwarding
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="twilio_phone_number">Twilio Phone Number *</Label>
                  <Input
                    id="twilio_phone_number"
                    placeholder="+16317107600"
                    value={formData.twilio_phone_number}
                    onChange={(e) =>
                      setFormData({ ...formData, twilio_phone_number: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name *</Label>
                  <Input
                    id="display_name"
                    placeholder="Facebook Ads Campaign"
                    value={formData.display_name}
                    onChange={(e) =>
                      setFormData({ ...formData, display_name: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="campaign_source">Campaign Source</Label>
                  <Input
                    id="campaign_source"
                    placeholder="facebook, newspaper, radio"
                    value={formData.campaign_source}
                    onChange={(e) =>
                      setFormData({ ...formData, campaign_source: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="county">County</Label>
                  <Select
                    value={formData.county}
                    onValueChange={(value) =>
                      setFormData({ ...formData, county: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Suffolk">Suffolk</SelectItem>
                      <SelectItem value="Nassau">Nassau</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-center justify-between pt-8">
                  <Label htmlFor="is_active">Active</Label>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Additional information about this phone number..."
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingNumber ? "Update" : "Add"} Phone Number
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg p-4 bg-muted/50">
        <h3 className="font-semibold mb-2">Twilio Webhook URL</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Use this webhook URL in your Twilio console for all phone numbers
        </p>
        <div className="flex gap-2">
          <Input
            value="https://ltugnhpocjzhouvvciek.supabase.co/functions/v1/twilio-voice-webhook"
            readOnly
            className="font-mono text-sm"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={copyWebhookUrl}
          >
            {copiedWebhook ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading phone numbers...</div>
      ) : phoneNumbers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">No phone numbers found. Add your first one to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Forward To</TableHead>
                  <TableHead>County</TableHead>
                  <TableHead>SMS</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phoneNumbers.map((number) => (
                  <TableRow key={number.id}>
                    <TableCell className="font-mono">
                      {formatPhoneNumber(number.twilio_phone_number)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {number.display_name}
                    </TableCell>
                    <TableCell>
                      {number.campaign_source || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatPhoneNumber(number.actual_forward_number || number.forward_to_number)}
                    </TableCell>
                    <TableCell>{number.county}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          number.sms_enabled
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                        }`}
                      >
                        {number.sms_enabled ? "Enabled" : "Disabled"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          number.is_active
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                        }`}
                      >
                        {number.is_active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(number)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleVoiceSettings(number)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Voice Settings
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(number.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {phoneNumbers.map((number) => (
              <Card key={number.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        {formatPhoneNumber(number.twilio_phone_number)}
                      </CardTitle>
                      <p className="text-sm font-medium text-muted-foreground">
                        {number.display_name}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(number)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleVoiceSettings(number)}>
                          <Settings className="h-4 w-4 mr-2" />
                          Voice Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(number.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-medium">Campaign:</span>{' '}
                      {number.campaign_source || '—'}
                    </div>
                    <div>
                      <span className="font-medium">County:</span> {number.county}
                    </div>
                    <div className="col-span-2">
                      <span className="font-medium">Forward To:</span>{' '}
                      <span className="font-mono">{formatPhoneNumber(number.actual_forward_number || number.forward_to_number)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 flex-wrap">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        number.sms_enabled
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                      }`}
                    >
                      SMS: {number.sms_enabled ? "On" : "Off"}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        number.is_active
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {number.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {voiceSettingsNumber && (
        <VoiceSettingsDialog
          phoneNumberId={voiceSettingsNumber.id}
          phoneNumberDisplay={voiceSettingsNumber.display_name}
          open={isVoiceSettingsOpen}
          onOpenChange={(open) => {
            setIsVoiceSettingsOpen(open);
            if (!open) {
              loadPhoneNumbers();
            }
          }}
        />
      )}
    </div>
  );
};
